import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Resolves the total cart item count from a cart-route response.
 * `/cart/add.js` returns only the added line items (no `item_count`), so fall
 * back to the `[ref="cartItemCount"]` value rendered inside the returned
 * section HTML.
 * @param {{ item_count?: number, sections?: Record<string, string> }} cart
 * @returns {number}
 */
function resolveCartItemCount(cart) {
  if (typeof cart?.item_count === 'number') return cart.item_count;

  const sections = cart?.sections;
  if (sections) {
    for (const html of Object.values(sections)) {
      if (typeof html !== 'string') continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent;
      if (countText != null && countText.trim() !== '') {
        return parseInt(countText, 10) || 0;
      }
    }
  }

  return 0;
}

/**
 * A continuous run of fence panels needs one more post than panels.
 * @param {number} totalFenceQty
 * @returns {number}
 */
function getRecommendedPostQty(totalFenceQty) {
  return totalFenceQty > 0 ? totalFenceQty + 1 : 0;
}

class PostSuggestionComponent extends Component {
  openPostModal(event) {
    const btn = event.target.closest('[data-post-type]');
    if (!btn) return;
    const modal = this.querySelector('.post-modal[data-post-type="' + btn.dataset.postType + '"]');
    if (!modal) return;

    // Reset all fence quantities to their original values and sum them up
    let totalFenceQty = 0;
    modal.querySelectorAll('[data-role="fence"] .post-modal__qty-input').forEach((input) => {
      const orig = Number(input.dataset.originalQty) || 1;
      input.value = orig;
      totalFenceQty += orig;
    });

    const postInput = modal.querySelector('[data-role="post"] .post-modal__qty-input');
    const screwsInput = modal.querySelector('[data-role="screws"] .post-modal__qty-input');
    const recommendedPosts = getRecommendedPostQty(totalFenceQty);
    const recommendedScrews = totalFenceQty * 4;
    if (postInput) postInput.value = recommendedPosts;
    if (screwsInput) screwsInput.value = recommendedScrews;
    this._updateSuggestedQty(modal, 'post', recommendedPosts);
    this._updateSuggestedQty(modal, 'screws', recommendedScrews);

    this._clearMismatch(modal);
    modal.hidden = false;
  }

  closeModal() {
    this.querySelectorAll('.post-modal:not([hidden])').forEach((m) => {
      m.classList.add('is-closing');
      m.addEventListener('animationend', () => {
        m.classList.remove('is-closing');
        m.hidden = true;
      }, { once: true });
    });
  }

  changeQty(event) {
    const btn = event.target.closest('[data-qty-change]');
    if (!btn) return;
    const itemEl = btn.closest('.post-modal__item');
    const input = btn.closest('.post-modal__qty')?.querySelector('.post-modal__qty-input');
    if (!input) return;

    const isFence = itemEl?.dataset.role === 'fence';
    const min = isFence ? 1 : 0;
    const next = Math.max(min, (Number(input.value) || 0) + Number(btn.dataset.qtyChange));
    input.value = next;

    const modal = btn.closest('.post-modal');
    if (!modal) return;

    if (isFence) {
      // Sum ALL fence quantities to recalculate recommended accessories
      let totalFenceQty = 0;
      modal.querySelectorAll('[data-role="fence"] .post-modal__qty-input').forEach((inp) => {
        totalFenceQty += Number(inp.value) || 0;
      });
      const postInput = modal.querySelector('[data-role="post"] .post-modal__qty-input');
      const screwsInput = modal.querySelector('[data-role="screws"] .post-modal__qty-input');
      const recommendedPosts = getRecommendedPostQty(totalFenceQty);
      const recommendedScrews = totalFenceQty * 4;
      if (postInput) postInput.value = recommendedPosts;
      if (screwsInput) screwsInput.value = recommendedScrews;
      this._updateSuggestedQty(modal, 'post', recommendedPosts);
      this._updateSuggestedQty(modal, 'screws', recommendedScrews);
      this._clearMismatch(modal);
    } else {
      this._updateMismatch(modal);
    }
  }

  _clearMismatch(modal) {
    modal.querySelectorAll('.post-modal__qty-input.is-mismatched').forEach((el) => el.classList.remove('is-mismatched'));
    modal.querySelectorAll('.post-modal__mismatch').forEach((el) => { el.hidden = true; });
  }

  _updateSuggestedQty(modal, role, quantity) {
    const suggestedQty = modal.querySelector(`[data-role="${role}"] .post-modal__suggested-qty`);
    if (suggestedQty) suggestedQty.textContent = String(quantity);
  }

  _updateMismatch(modal) {
    let totalFenceQty = 0;
    modal.querySelectorAll('[data-role="fence"] .post-modal__qty-input').forEach((inp) => {
      totalFenceQty += Number(inp.value) || 0;
    });

    const postInput = modal.querySelector('[data-role="post"] .post-modal__qty-input');
    const screwsInput = modal.querySelector('[data-role="screws"] .post-modal__qty-input');
    if (postInput) {
      const mismatch = Number(postInput.value) !== getRecommendedPostQty(totalFenceQty);
      postInput.classList.toggle('is-mismatched', mismatch);
      const warn = modal.querySelector('[data-role="post"] .post-modal__mismatch');
      if (warn) warn.hidden = !mismatch;
    }
    if (screwsInput) {
      const mismatch = Number(screwsInput.value) !== totalFenceQty * 4;
      screwsInput.classList.toggle('is-mismatched', mismatch);
      const warn = modal.querySelector('[data-role="screws"] .post-modal__mismatch');
      if (warn) warn.hidden = !mismatch;
    }
  }

  async handleUpdateToMatch(event) {
    const btn = event.target.closest('button');
    if (!btn || btn.disabled) return;
    const modal = btn.closest('.post-modal');
    if (!modal) return;

    btn.disabled = true;

    const postVariantId = Number(modal.dataset.postVariantId) || 0;
    const screwsVariantId = Number(modal.dataset.screwsVariantId) || 0;
    const postDeliveryTime = modal.dataset.postDeliveryTime ?? '';
    const screwsDeliveryTime = modal.dataset.screwsDeliveryTime ?? '';
    const postQty = Number(modal.querySelector('[data-role="post"] .post-modal__qty-input')?.value) || 0;
    const screwsQty = Number(modal.querySelector('[data-role="screws"] .post-modal__qty-input')?.value) || 0;

    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sections.add(el.dataset.sectionId);
    });
    const sectionsParam = Array.from(sections).join(',');

    try {
      // 1. Update each fence item quantity if changed
      for (const fenceEl of modal.querySelectorAll('[data-role="fence"]')) {
        const fenceInput = fenceEl.querySelector('.post-modal__qty-input');
        const originalQty = Number(fenceInput?.dataset.originalQty) || 0;
        const newQty = Number(fenceInput?.value) || 0;
        const itemKey = fenceEl.dataset.itemKey;
        if (itemKey && newQty > 0 && newQty !== originalQty) {
          await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
            body: JSON.stringify({ id: itemKey, quantity: newQty, sections: sectionsParam, sections_url: window.location.pathname }),
          }));
        }
      }

      if (!postVariantId && !screwsVariantId) {
        this.closeModal();
        btn.disabled = false;
        return;
      }

      // 2. Fetch current cart to check existing line items (avoid accumulating qty)
      const cartData = await fetch('/cart.js').then((r) => r.json());

      const findExisting = (variantId, fenceGroup) =>
        cartData.items.find(
          (ci) =>
            ci.variant_id === variantId &&
            String(ci.properties?._fence_group ?? '') === String(fenceGroup)
        );

      const changeItems = [];
      const addItems = [];

      if (postVariantId && postQty > 0) {
        const existing = findExisting(postVariantId, postVariantId);
        if (existing) {
          changeItems.push({ key: existing.key, quantity: postQty });
        } else {
          addItems.push({ id: postVariantId, quantity: postQty, properties: { _fence_group: String(postVariantId), _delivery_time: postDeliveryTime } });
        }
      }

      if (screwsVariantId && screwsQty > 0) {
        const existing = findExisting(screwsVariantId, postVariantId);
        if (existing) {
          changeItems.push({ key: existing.key, quantity: screwsQty });
        } else {
          addItems.push({ id: screwsVariantId, quantity: screwsQty, properties: { _fence_group: String(postVariantId), _delivery_time: screwsDeliveryTime } });
        }
      }

      // 3. Apply changes sequentially (each change triggers no section re-render except the last)
      for (const { key, quantity } of changeItems) {
        await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          body: JSON.stringify({ id: key, quantity }),
        }));
      }

      // 4. Add new items (last request gets sections for re-render)
      if (addItems.length > 0) {
        const addBody = JSON.stringify({ items: addItems, sections: sectionsParam, sections_url: window.location.pathname });
        const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: addBody }));
        const cart = await response.json();
        // `/cart/add.js` returns the added line items, NOT the whole cart, so
        // `cart.item_count` is undefined here. Resolve the real count from the
        // re-rendered section HTML so the cart bubble doesn't drop to 0.
        const itemCount = resolveCartItemCount(cart);
        this.closeModal();
        this.dispatchEvent(new CartAddEvent(cart, this.id, { source: 'post-suggestion-component', itemCount, sections: cart.sections }));
        return;
      }

      // If only changes (no new adds), trigger re-render via a final change with sections
      if (changeItems.length > 0) {
        const last = changeItems[changeItems.length - 1];
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          body: JSON.stringify({ id: last.key, quantity: last.quantity, sections: sectionsParam, sections_url: window.location.pathname }),
        }));
        const cart = await response.json();
        this.closeModal();
        this.dispatchEvent(new CartAddEvent(cart, this.id, { source: 'post-suggestion-component', itemCount: cart.item_count, sections: cart.sections }));
        return;
      }

      this.closeModal();
    } catch (error) {
      console.error('[post-suggestion] update to match failed', error);
    } finally {
      btn.disabled = false;
    }
  }
}

if (!customElements.get('post-suggestion-component')) {
  customElements.define('post-suggestion-component', PostSuggestionComponent);
}
